import { useState } from 'react';
import { Chip } from 'primereact/chip';
import { Divider } from 'primereact/divider';
import { Finding } from '@/api/domains/analyses/types';
import styles from './FindingHeader.module.css';
import { getFindingTypeConfig, getSeverityColor } from '../../../utils';
import Dialog from '@/components/ui/Dialog/Dialog';
import dialogStyles from '@/components/ui/Dialog/Dialog.module.css';

interface FindingHeaderProps {
  finding: Finding;
  currentIndex: number;
  totalCount: number;
  onCopy: (text: string, target: string) => void;
  copiedTarget: string | null;
}

export const FindingHeader = ({
  finding,
  currentIndex,
  totalCount
}: FindingHeaderProps) => {
  const [infoDialog, setInfoDialog] = useState<{ visible: boolean }>({
    visible: false
  });

  const showInfo = () => setInfoDialog({ visible: true });
  const renderDialogHeader = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: 'white'
      }}
    >
      <i className="pi pi-info-circle" />
      <span>Finding Types</span>
    </div>
  );

  const renderDialogContent = () => (
    <div className={dialogStyles.dialogBodyWrapper}>
      <div className={dialogStyles.dialogContent}>
        <p>
          <strong>Explanation of finding types</strong>
        </p>
        <ul>
          <li>
            <strong>ALIGNMENT</strong> (requirements are essentially equivalent)
          </li>
          <li>
            <strong>GAP</strong> (Doc1 contains obligations missing in Doc2)
          </li>
          <li>
            <strong>CONFLICT</strong> (differences that may cause inconsistency,
            but not a direct logical contradiction)
          </li>
          <li>
            <strong>SUPPLEMENT</strong> (Doc2 contains additional, consistent
            requirements not in Doc1)
          </li>
          <li>
            <strong>CONTRADICTION</strong> (Doc1 and Doc2 contain logically
            incompatible requirements)
          </li>
        </ul>
      </div>
    </div>
  );

  const typeConfig = getFindingTypeConfig(finding.finding_type);
  const severityColor = getSeverityColor(finding.severity);
  const vectorSimilarity = finding.vector_similarity ?? 0;
  const llmConfidence = finding.rag_insight?.llm_confidence ?? 0;
  const confidenceLabel =
    llmConfidence >= 0.8 ? 'High' : llmConfidence >= 0.7 ? 'Medium' : 'Low';

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'pi-exclamation-circle';
      case 'HIGH':
        return 'pi-exclamation-triangle';
      case 'MEDIUM':
        return 'pi-info-circle';
      case 'LOW':
        return 'pi-minus-circle';
      default:
        return 'pi-circle';
    }
  };

  return (
    <div className={styles.header}>
      <div className={styles.titleRow}>
        <div className={styles.titleLeft}>
          <span className={styles.findingCount}>
            Finding {currentIndex + 1} of {totalCount}
          </span>
          <div className={styles.chips}>
            <Chip
              label={finding.finding_type}
              icon={`pi ${typeConfig.icon}`}
              className={styles.typeChip}
              style={{
                background: `${typeConfig.color}22`,
                color: typeConfig.color,
                border: `1.5px solid ${typeConfig.color}44`
              }}
            />
            <i
              className={`pi pi-question-circle`}
              style={{
                color: 'rgba(255,255,255,0.35)',
                cursor: 'pointer'
              }}
              title="What do these finding types mean?"
              onClick={showInfo}
            />
            <Chip
              label={finding.severity}
              icon={`pi ${getSeverityIcon(finding.severity)}`}
              className={styles.severityChip}
              style={{
                background: `${severityColor.background}22`,
                color: severityColor.color,
                border: `1.5px solid ${severityColor.color}44`
              }}
            />
          </div>
        </div>
      </div>

      <Dialog
        header={renderDialogHeader()}
        visible={infoDialog.visible}
        onHide={() => setInfoDialog({ visible: false })}
        className={dialogStyles.customDialog}
        draggable={false}
        modal
        maximizable={false}
        contentStyle={{ padding: '0px' }}
        headerStyle={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}
      >
        {renderDialogContent()}
      </Dialog>

      <Divider className={styles.divider} />

      <div className={styles.metadataRow}>
        <div className={styles.metadataItem}>
          <div className={styles.metadataHeader}>
            <i className="pi pi-sitemap" />
            <span
              className={styles.metadataLabel}
              title="How closely this finding matches a section in the source document (0–1). Higher is better."
            >
              Match Score
            </span>
          </div>
          <span className={styles.metadataValue}>
            {vectorSimilarity.toFixed(3)} ({(vectorSimilarity * 100).toFixed(1)}
            %)
          </span>
          {vectorSimilarity === 0 && (
            <span className={styles.metadataNote}>
              No matching section found in source document
            </span>
          )}
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataHeader}>
            <i className="pi pi-chart-line" />
            <span
              className={styles.metadataLabel}
              title="Estimated confidence of the LLM that this finding is relevant."
            >
              AI Confidence (est.)
            </span>
          </div>
          <span
            className={styles.metadataValue}
            style={{
              color:
                llmConfidence >= 0.8
                  ? '#4caf50'
                  : llmConfidence >= 0.7
                    ? '#ff9800'
                    : '#f44336'
            }}
          >
            {confidenceLabel} ({Math.round(llmConfidence * 100)}%)
          </span>
        </div>
      </div>
    </div>
  );
};
